import { z } from 'zod';

/**
 * Chat (Operations M2, docs/specs/operations.md §9, D15/D71): channels derived from the
 * organisation and seats, ad-hoc channels, direct messages and one thread per record, delivered
 * over the realtime WebSocket (`/api/v1/realtime`).
 */
export const CHAT_PERMISSIONS = {
  use: 'chat.messages.use',
  admin: 'chat.admin.manage',
  /** Remove anyone's message (D73): shown as "Removed by a moderator", audited. */
  moderate: 'chat.messages.moderate',
} as const;

export const CHAT_MODERATION_REASON_MAX = 500;
/** Default retention for channel and DM messages (chat_settings.retention_months, D73). */
export const CHAT_RETENTION_MONTHS_DEFAULT = 24;

/** Edits are allowed for this long after posting. */
export const CHAT_EDIT_WINDOW_MS = 15 * 60 * 1000;
export const CHAT_MESSAGE_MAX = 4000;
export const CHAT_PAGE_SIZE = 50;

export const ChatChannelKindSchema = z.enum([
  'site',
  'region',
  'managers',
  'adhoc',
  'dm',
  'record',
]);
export type ChatChannelKind = z.infer<typeof ChatChannelKindSchema>;

/** The records that carry a discussion thread. */
export const ChatRecordTypeSchema = z.enum([
  'holiday',
  'absence',
  'work_order',
  'property_issue',
  'fleet_case',
  'attendance_exception',
  'safety_incident',
  /** S-M2: a customer account (`customers.id`). */
  'customer',
]);
export type ChatRecordType = z.infer<typeof ChatRecordTypeSchema>;

export const ChatPersonSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  initials: z.string(),
});
export type ChatPerson = z.infer<typeof ChatPersonSchema>;

export const ChatAttachmentSchema = z.object({
  fileId: z.uuid(),
  filename: z.string(),
  mime: z.string(),
  sizeBytes: z.number().int().min(0),
});
export type ChatAttachment = z.infer<typeof ChatAttachmentSchema>;

export const ChatMessageSchema = z.object({
  id: z.uuid(),
  channelId: z.uuid(),
  seq: z.number().int().min(1),
  kind: z.enum(['text', 'system']),
  author: ChatPersonSchema.nullable(),
  /** Empty when deleted. */
  body: z.string(),
  mentions: z.array(ChatPersonSchema),
  attachments: z.array(ChatAttachmentSchema),
  createdAt: z.iso.datetime(),
  editedAt: z.iso.datetime().nullable(),
  deleted: z.boolean(),
  /** Deleted by a moderator rather than its author ("Removed by a moderator"). */
  removedByModerator: z.boolean().default(false),
  /** For the caller: own text message, not deleted, within the edit window. */
  canEdit: z.boolean(),
  canDelete: z.boolean(),
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

export const ChatRecordRefSchema = z.object({
  type: ChatRecordTypeSchema,
  id: z.uuid(),
  title: z.string(),
  link: z.string(),
});
export type ChatRecordRef = z.infer<typeof ChatRecordRefSchema>;

export const ChatChannelSummarySchema = z.object({
  id: z.uuid(),
  kind: ChatChannelKindSchema,
  name: z.string(),
  description: z.string().nullable(),
  /** DMs: the other person. */
  dmPerson: ChatPersonSchema.nullable(),
  record: ChatRecordRefSchema.nullable(),
  memberCount: z.number().int().min(0),
  lastSeq: z.number().int().min(0),
  lastReadSeq: z.number().int().min(0),
  unread: z.number().int().min(0),
  lastMessageAt: z.iso.datetime().nullable(),
  lastMessage: z.object({ authorName: z.string().nullable(), preview: z.string() }).nullable(),
  archived: z.boolean(),
  canPost: z.boolean(),
});
export type ChatChannelSummary = z.infer<typeof ChatChannelSummarySchema>;

/** GET /chat/channels: everything in the chat sidebar. */
export const ChatOverviewSchema = z.object({
  channels: z.array(ChatChannelSummarySchema),
  dms: z.array(ChatChannelSummarySchema),
  threads: z.array(ChatChannelSummarySchema),
  totalUnread: z.number().int().min(0),
  canUse: z.boolean(),
  canAdmin: z.boolean(),
  /** chat.messages.moderate: may remove anyone's message. */
  canModerate: z.boolean().default(false),
});
export type ChatOverview = z.infer<typeof ChatOverviewSchema>;

/** GET /chat/channels/:id */
export const ChatChannelDetailSchema = z.object({
  channel: ChatChannelSummarySchema,
  /** Who can be @mentioned (members; for a record thread, its followers). */
  members: z.array(ChatPersonSchema),
  isMember: z.boolean(),
});
export type ChatChannelDetail = z.infer<typeof ChatChannelDetailSchema>;

/** GET /chat/channels/:id/messages?before=&after=&limit= (oldest first). */
export const ChatMessagesResponseSchema = z.object({
  messages: z.array(ChatMessageSchema),
  /** More older messages exist before the first one returned. */
  hasMore: z.boolean(),
});
export type ChatMessagesResponse = z.infer<typeof ChatMessagesResponseSchema>;

export const ChatPostSchema = z.object({
  body: z.string().max(CHAT_MESSAGE_MAX * 2),
  attachmentIds: z.array(z.uuid()).max(10).optional(),
});
export type ChatPost = z.infer<typeof ChatPostSchema>;

export const ChatEditSchema = z.object({ body: z.string().max(CHAT_MESSAGE_MAX * 2) });

/** POST /chat/messages/:id/moderate (chat.messages.moderate). */
export const ChatModerateSchema = z.object({
  reason: z.string().trim().min(1).max(CHAT_MODERATION_REASON_MAX),
});
export type ChatModerate = z.infer<typeof ChatModerateSchema>;

export const ChatReadSchema = z.object({ seq: z.number().int().min(0) });
export const ChatReadResponseSchema = z.object({
  lastReadSeq: z.number().int().min(0),
  unread: z.number().int().min(0),
  totalUnread: z.number().int().min(0),
});
export type ChatReadResponse = z.infer<typeof ChatReadResponseSchema>;

export const ChatUploadResponseSchema = z.object({ attachment: ChatAttachmentSchema });

/** GET /chat/records/:type/:id: the record's thread (created on first open). */
export const ChatRecordThreadSchema = z.object({
  channel: ChatChannelSummarySchema,
  members: z.array(ChatPersonSchema),
  following: z.boolean(),
});
export type ChatRecordThread = z.infer<typeof ChatRecordThreadSchema>;

export const ChatDmCreateSchema = z.object({ personId: z.uuid() });

/** GET /chat/people?q=: who the caller may start a direct message with. */
export const ChatPeopleResponseSchema = z.object({ people: z.array(ChatPersonSchema) });

export const ChatSearchResponseSchema = z.object({
  results: z.array(
    z.object({
      channel: z.object({ id: z.uuid(), kind: ChatChannelKindSchema, name: z.string() }),
      message: ChatMessageSchema,
    }),
  ),
});
export type ChatSearchResponse = z.infer<typeof ChatSearchResponseSchema>;

// Admin ---------------------------------------------------------------------------------

export const ChatAdhocWriteSchema = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).nullable().optional(),
  memberIds: z.array(z.uuid()).max(500),
});
export type ChatAdhocWrite = z.infer<typeof ChatAdhocWriteSchema>;

export const ChatAdminChannelSchema = z.object({
  id: z.uuid(),
  kind: ChatChannelKindSchema,
  name: z.string(),
  memberCount: z.number().int().min(0),
  messageCount: z.number().int().min(0),
  lastMessageAt: z.iso.datetime().nullable(),
  archived: z.boolean(),
});
export const ChatAdminChannelsSchema = z.object({ channels: z.array(ChatAdminChannelSchema) });
export type ChatAdminChannel = z.infer<typeof ChatAdminChannelSchema>;

export const ChatOkSchema = z.object({ ok: z.literal(true) });
export const ChatCreatedSchema = z.object({ id: z.uuid() });

// Realtime ------------------------------------------------------------------------------

/** Client → server frames on /api/v1/realtime. */
export const RealtimeClientFrameSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('ping') }),
  z.object({ type: z.literal('subscribe'), channelId: z.uuid() }),
  z.object({ type: z.literal('unsubscribe'), channelId: z.uuid() }),
]);
export type RealtimeClientFrame = z.infer<typeof RealtimeClientFrameSchema>;

/** Server → client frames. */
export const RealtimeServerFrameSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('hello'),
    personId: z.uuid(),
    /** Sent back as `X-Realtime-Connection` on writes so the sender's own echo is skipped. */
    connectionId: z.uuid(),
    heartbeatMs: z.number().int(),
  }),
  z.object({ type: z.literal('pong') }),
  z.object({ type: z.literal('subscribed'), channelId: z.uuid() }),
  z.object({ type: z.literal('error'), error: z.string(), channelId: z.uuid().optional() }),
  /** The person's unread bell count changed. */
  z.object({ type: z.literal('notifications'), unread: z.number().int().min(0) }),
  /** A new message in a channel the connection subscribed to. */
  z.object({ type: z.literal('chat.message'), message: ChatMessageSchema }),
  /** A message was edited, deleted or removed by a moderator. */
  z.object({ type: z.literal('chat.message.updated'), message: ChatMessageSchema }),
  /** Something new in one of the person's channels: refresh the list and unread counts. */
  z.object({
    type: z.literal('chat.activity'),
    channelId: z.uuid(),
    seq: z.number().int(),
    authorId: z.uuid().nullable(),
  }),
  /** The person read up to `seq` (in another tab or device). */
  z.object({ type: z.literal('chat.read'), channelId: z.uuid(), seq: z.number().int() }),
  /** The person's channel list changed (membership derived again, a DM or channel created). */
  z.object({ type: z.literal('chat.channels') }),
  /** `sales-sync` changed today's figures at these branches: open sales pages and Home refetch. */
  z.object({ type: z.literal('sales.updated'), siteIds: z.array(z.uuid()) }),
]);
export type RealtimeServerFrame = z.infer<typeof RealtimeServerFrameSchema>;
